import { getLocalTimeZone, parseAbsolute, ZonedDateTime } from '@internationalized/date'
import { DateTime } from 'luxon'
import { ReactNode, useMemo, useState } from 'react'
import {
	Button as AriaButton,
	CalendarCell,
	CalendarGrid,
	DateInput,
	DateRangePicker as AriaDateRangePicker,
	DateSegment,
	Dialog,
	Group,
	Heading,
	Label,
	Popover,
	RangeCalendar,
	CalendarGridHeader,
	CalendarGridBody,
	DateRangePickerProps,
} from 'react-aria-components'
import { parse } from '../datemath'
import { Button } from './button'

const cell = {
	base: 'w-full h-full grid place-items-center rounded-full forced-color-adjust-none outline outline-rose-400 outline-offset-4',
	selectionState: {
		none: 'group-hover:bg-gray-100 group-pressed:bg-gray-200',
		middle: [
			'group-hover:bg-blue-200',
			'group-invalid:group-hover:bg-red-200',
			'group-pressed:bg-blue-300',
			'group-invalid:group-pressed:bg-red-300',
		].join(' '),
		cap: 'bg-blue-400 group-invalid:bg-red-600 text-white',
	},
	isDisabled: 'text-gray-300',
}

export type MathDateRange = { start: string; end: string }
export type DateDateRange = { start: DateTime; end: DateTime }
export type ZonedDateRange = { start: ZonedDateTime; end: ZonedDateTime }
export type DateRange = MathDateRange | DateDateRange

export function isMathDateDange(range: DateRange | ZonedDateRange): range is MathDateRange {
	return typeof range.start === 'string'
}

export function dateRangeToDateDateRange(range: DateRange): DateDateRange {
	if (isMathDateDange(range)) {
		return {
			start: parse(range.start),
			end: parse(range.end).minus({ millisecond: 1 }),
		}
	}
	return range
}

export function dateRangeToPrimitive(range: DateRange) {
	const { start, end } = dateRangeToDateDateRange(range)
	return {
		start: start.toISO() as string,
		end: end.toISO() as string,
	}
}

function toRaValue(value?: DateRange) {
	if (!value) return null
	const timeZone = getLocalTimeZone()
	const range = dateRangeToDateDateRange(value)
	const start = range.start.toISO()
	const end = range.end.toISO()
	if (!start || !end) return null
	return {
		start: parseAbsolute(start, timeZone),
		end: parseAbsolute(end, timeZone),
	}
}

export function DateRangePicker({
	label,
	value,
	onChange,
	presets = 'forward',
	...props
}: {
	label: ReactNode
	value?: DateRange
	onChange: (range: DateRange) => void
	presets?: 'forward' | 'backward' | 'both' | 'none'
} & Omit<DateRangePickerProps<ZonedDateTime>, 'value' | 'onChange'>) {
	const raValue = useMemo(() => toRaValue(value), [value])

	const [localValue, setLocalValue] = useState(raValue ?? null)

	const raOnChange = (value: MathDateRange | { start: ZonedDateTime; end: ZonedDateTime }) => {
		if (!value) {
			setLocalValue(null)
			return
		}
		if (isMathDateDange(value)) {
			setLocalValue(toRaValue(value))
			onChange(value)
			return
		}
		setLocalValue(value)
		const start = DateTime.fromISO(value.start.toString())
		const end = DateTime.fromISO(value.end.toString()).endOf('day')
		onChange({ start, end })
	}

	return (
		<AriaDateRangePicker
			className={`space-y-1`}
			granularity="day"
			value={raValue ?? localValue}
			onChange={raOnChange}
			{...props}
		>
			{label && <Label className="flex font-normal leading-tight text-xs">{label}</Label>}
			<Group className="flex">
				<div className="flex flex-auto w-max items-start gap-2 bg-transparent text-sm text-gray-700 font-sans font-normal outline outline-0 disabled:border-gray-100 border border-gray-400 p-2 rounded-l focus:border-gray-900">
					<DateInput className="flex" slot="start">
						{segment => <DateSegment segment={segment} />}
					</DateInput>
					<span aria-hidden="true">–</span>
					<DateInput className="flex" slot="end">
						{segment => <DateSegment segment={segment} />}
					</DateInput>
				</div>
				<Button
					variant="INVISIBLE"
					size="SMALL"
					className="border border-gray-400 rounded-l-none"
				>
					▼
				</Button>
			</Group>
			<Popover className="bg-white rounded border border-gray-200 shadow-xl shadow-gray-200 p-2">
				<Dialog className="flex gap-4">
					<ul className="flex flex-col gap-1 min-w-max text-right">
						{getPresets(presets, 'left').map((preset, index) => (
							<li key={index}>
								<Button
									variant="TEXT"
									size="TEXT"
									onPress={() => raOnChange(preset.value)}
								>
									{preset.label}
								</Button>
							</li>
						))}
					</ul>
					<RangeCalendar>
						<header className="flex space-between mb-4">
							<AriaButton slot="previous">◀</AriaButton>
							<Heading className="m-auto" />
							<AriaButton slot="next">▶</AriaButton>
						</header>
						<CalendarGrid className="[&_td]:px-0">
							<CalendarGridHeader className="bg-blue-50">
								{date => (
									<th className="p-1 text-gray-800 text-sm text-center uppercase font-light border-slate-200">
										{date}
									</th>
								)}
							</CalendarGridHeader>
							<CalendarGridBody>
								{date => (
									<CalendarCell
										date={date}
										className="group w-8 h-8 p-0 grid place-items-center text-sm outline outline-0 cursor-default outside-month:text-gray-300 selected:bg-blue-100 invalid:selected:bg-red-100 [td:first-child_&]:rounded-s-full selection-start:rounded-s-full [td:last-child_&]:rounded-e-full selection-end:rounded-e-full"
									>
										{({
											formattedDate,
											isSelected,
											isSelectionStart,
											isSelectionEnd,
											isFocusVisible,
											isDisabled,
										}) => (
											<span
												className={`${cell.base} ${
													isSelected
														? isSelectionStart || isSelectionEnd
															? cell.selectionState.cap
															: cell.selectionState.middle
														: cell.selectionState.none
												}
												${isDisabled ? cell.isDisabled : ''}
												${isFocusVisible ? 'outline-2' : 'outline-0'}
											`}
											>
												{formattedDate}
											</span>
										)}
									</CalendarCell>
								)}
							</CalendarGridBody>
						</CalendarGrid>
					</RangeCalendar>
					<ul className="flex flex-col gap-1 min-w-max text-right">
						{getPresets(presets, 'right').map((preset, index) => (
							<li key={index}>
								<Button
									variant="TEXT"
									size="TEXT"
									onPress={() => raOnChange(preset.value)}
								>
									{preset.label}
								</Button>
							</li>
						))}
					</ul>
				</Dialog>
			</Popover>
		</AriaDateRangePicker>
	)
}

function getPresets(presets: 'forward' | 'backward' | 'both' | 'none', side: 'left' | 'right') {
	if (presets === 'forward' && side === 'right') return PRESETS_FORWARD
	if (presets === 'backward' && side === 'left') return PRESETS_BACKWARD
	if (presets === 'both') {
		if (side === 'left') return PRESETS_BACKWARD
		if (side === 'right') return PRESETS_FORWARD
	}
	if (presets === 'none') return []
	return []
}

export const PRESETS_FORWARD: Array<{
	label: string
	value: MathDateRange
}> = [
	{
		label: 'Dnes',
		value: {
			start: 'now/d',
			end: 'now+1d/d',
		},
	},
	{
		label: 'Zítra',
		value: {
			start: 'now+1d/d',
			end: 'now+2d/d',
		},
	},
	{
		label: 'Tento týden',
		value: {
			start: 'now/w',
			end: 'now+1w/w',
		},
	},
	{
		label: 'Tento měsíc',
		value: {
			start: 'now/M',
			end: 'now+1M/M',
		},
	},
	{
		label: 'Příštích 7 dní',
		value: {
			start: 'now/d',
			end: 'now+7d/d',
		},
	},
	{
		label: 'Příštích 14 dní',
		value: {
			start: 'now/d',
			end: 'now+14d/d',
		},
	},
	{
		label: 'Příštích 30 dní',
		value: {
			start: 'now/d',
			end: 'now+30d/d',
		},
	},
	{
		label: 'Příštích 60 dní',
		value: {
			start: 'now/d',
			end: 'now+60d/d',
		},
	},
	{
		label: 'Příštích 90 dní',
		value: {
			start: 'now/d',
			end: 'now+90d/d',
		},
	},
]

export const PRESETS_BACKWARD: Array<{
	label: string
	value: MathDateRange
}> = [
	{
		label: 'Dnes',
		value: {
			start: 'now/d',
			end: 'now+1d/d',
		},
	},
	{
		label: 'Včera',
		value: {
			start: 'now-1d/d',
			end: 'now/d',
		},
	},
	{
		label: 'Poslední týden',
		value: {
			start: 'now-1w/w',
			end: 'now/w',
		},
	},
	{
		label: 'Poslední měsíc',
		value: {
			start: 'now-1M/M',
			end: 'now/M',
		},
	},
	{
		label: 'Tento rok',
		value: {
			start: 'now/y',
			end: 'now+1y/y',
		},
	},
	{
		label: 'Posledních 7 dní',
		value: {
			start: 'now-7d/d',
			end: 'now/d',
		},
	},
	{
		label: 'Posledních 30 dní',
		value: {
			start: 'now-30d/d',
			end: 'now/d',
		},
	},
	{
		label: 'Posledních 60 dní',
		value: {
			start: 'now-60d/d',
			end: 'now/d',
		},
	},
	{
		label: 'Posledních 90 dní',
		value: {
			start: 'now-90d/d',
			end: 'now/d',
		},
	},
]
